import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from 'react'
import { useAuth } from './auth-context'

interface HealthCheck {
  status: string
  service: string
  timestamp: string
  duration: string
  data: any
  error: string | null
}

export interface HealthCheckStatus {
  isHealthy: boolean
  lastChecked: Date | null
  error: string | null
  isLoading: boolean
  checks: HealthCheck[]
}

interface HealthCheckContextType {
  healthStatus: HealthCheckStatus
  checkHealth: () => Promise<void>
  startHealthCheck: () => void
  stopHealthCheck: () => void
}

const HealthCheckContext = createContext<HealthCheckContextType>({
  healthStatus: {
    isHealthy: false,
    lastChecked: null,
    error: null,
    isLoading: false,
    checks: [],
  },
  checkHealth: async () => { },
  startHealthCheck: () => { },
  stopHealthCheck: () => { },
})

export const useHealthCheck = () => useContext(HealthCheckContext)

interface HealthCheckProviderProps {
  children: ReactNode
}

const HEALTH_CHECK_INTERVAL = 300000 // 300 seconds
export const HealthCheckProvider: React.FC<HealthCheckProviderProps> = ({
  children,
}) => {
  const [healthStatus, setHealthStatus] = useState<HealthCheckStatus>({
    isHealthy: false,
    lastChecked: null,
    error: null,
    isLoading: false,
    checks: [],
  })

  const { clientId, tokens, user, isLoading, logout } = useAuth()

  // Use ref to store interval ID - this prevents stale closure issues
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isHealthCheckActive = useRef(false)
  const baseApiUrl: string = process.env.REACT_APP_API_BASE_URL ?? ''

  const checkHealth = useCallback(async (): Promise<void> => {
    // Prevent concurrent health checks
    if (isHealthCheckActive.current) {
      return
    }

    isHealthCheckActive.current = true
    setHealthStatus((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      const token = tokens?.accessToken

      if (!token) {
        logout()
        return
      }

      const response = await fetch(`${baseApiUrl}/health`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        // Add timeout to prevent hanging requests
        signal: AbortSignal.timeout(10000), // 10 second timeout
      })

      if (response.status === 401) {
        logout()
        return
      }

      if (!response.ok) {
        throw new Error(
          `Health check failed: ${response.status} ${response.statusText}`
        )
      }

      const healthData = await response.json()

      setHealthStatus({
        isHealthy: healthData.overallStatus === 'Healthy',
        lastChecked: new Date(healthData.timestamp),
        error: null,
        isLoading: false,
        checks: healthData.checks || [],
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erro desconhecido'

      setHealthStatus({
        isHealthy: false,
        lastChecked: new Date(),
        error: errorMessage,
        isLoading: false,
        checks: [],
      })
    } finally {
      isHealthCheckActive.current = false
    }
  }, [baseApiUrl, tokens, logout])

  const startHealthCheck = useCallback(() => {
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    // Run immediately first
    checkHealth()

    // Then set up the interval
    intervalRef.current = setInterval(() => {
      checkHealth()
    }, HEALTH_CHECK_INTERVAL)
  }, [checkHealth])

  const stopHealthCheck = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  // Initialize health check context
  useEffect(() => {
    if (clientId) {
      console.info('🏥 HealthCheck Context initialized 🚗🔥')
    }
  }, [clientId])

  // Main effect to manage health check lifecycle
  useEffect(() => {
    // Only start health check when we have all required auth data
    const isAuthenticated = !!(clientId && user && tokens && !isLoading)

    if (isAuthenticated) {
      startHealthCheck()
    } else {
      stopHealthCheck()
    }

    // Cleanup on unmount or when authentication state changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [clientId, startHealthCheck, stopHealthCheck]) // Only depend on clientId, not user/tokens/isLoading

  return (
    <HealthCheckContext.Provider
      value={{
        healthStatus,
        checkHealth,
        startHealthCheck,
        stopHealthCheck,
      }}
    >
      {children}
    </HealthCheckContext.Provider>
  )
}
